import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import { type JsonObject, RowId } from "@tento-lona/sheets";
import { RowOptionsElement } from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import {
  CANVAS_HRULE_SPEC,
  CANVAS_POINTS_SPEC,
  CANVAS_LINE_SPEC,
  CANVAS_BAR_SPEC,
  CANVAS_FUNNEL_SPEC,
  CANVAS_REGRESSION_CURVE_SPEC,
  CANVAS_REGRESSION_BAND_SPEC,
  CANVAS_REGRESSION_FILL_SPEC,
} from "@tento-lona/sheets/platform";
import { DataRendererPluginBase } from "./canvas-base.ts";
import { deepFrozen } from "@tento-lona/sheets/internal-ui";

/**
 * The one canvas renderer DOM plugin. Every canvas slot id
 * (`canvas.hrule`, `canvas.points`, `canvas.line`, `canvas.bar`,
 * `canvas.funnel`, `canvas.candlestick`, `canvas.regression-*`) shares
 * byte-identical behavior — see `DataRendererPluginBase`, whose
 * `onBindRow` / content provider read the row's `type` regardless of
 * slot. The only per-id difference is the `(id, role)` spec, so a
 * single class carries the id as data instead of one boilerplate
 * subclass per chart type.
 */
export class CanvasRendererPlugin extends DataRendererPluginBase {
  /**
   * The primary canvas slot's spec (`canvas.hrule`), which a host passes as
   * `new CanvasRendererPlugin(CanvasRendererPlugin.HRULE, …)`. The spec
   * constant itself is `CANVAS_HRULE_SPEC` on `@tento/lona/sheets/platform`.
   */
  static readonly HRULE: PerTypeSpec = CANVAS_HRULE_SPEC;

  readonly id: string;

  constructor(
    spec: PerTypeSpec,
    makeRowOptions: RowOptionsElement.Factory,
    patchRowAttributes: (
      rowId: RowId,
      attributes: JsonObject,
    ) => void,
  ) {
    super(makeRowOptions, patchRowAttributes);
    this.id = spec.id;
  }
}

/**
 * Sibling canvas renderers the data-canvas paints alongside its
 * primary `canvas.hrule` dataRenderer (which carries its own patch
 * channel). The app registers one `CanvasRendererPlugin` per spec,
 * sharing the primary's row-options + attribute-patch channel.
 */
export const CANVAS_RENDERER_SIBLING_SPECS: readonly PerTypeSpec[] = deepFrozen([
  CANVAS_POINTS_SPEC,
  CANVAS_LINE_SPEC,
  CANVAS_BAR_SPEC,
  CANVAS_FUNNEL_SPEC,
  CANVAS_REGRESSION_CURVE_SPEC,
  CANVAS_REGRESSION_BAND_SPEC,
  CANVAS_REGRESSION_FILL_SPEC,
]);
